// Shared helpers for the Services catalog — used by both the super-admin
// catalog page and the org-admin services page so name-casing and due-rule
// wording stay identical across the two.

// --- Service name display -------------------------------------------------
// The source catalog is SCREAMING_CASE throughout. Rendering it verbatim
// reads like a raw data dump, so we title-case for display only — the
// underlying data (and search) still matches on the original strings.
// Recognized acronyms stay upper; small linking words stay lowercase
// (except as the first word); anything glued to digits/punctuation
// (e.g. "GSTR-1", "26Q", "CO.OP", "(EC)") is left untouched.
const SMALL_WORDS = new Set(['of', 'in', 'by', 'to', 'and', 'on', 'at', 'for', 'the', 'a', 'an', 'or', 'from', 'with', 'within']);
const KEEP_UPPER = new Set([
  'tds', 'pan', 'tan', 'itr', 'roc', 'llp', 'din', 'fema', 'lodr', 'nclt', 'aoa', 'moa', 'rd',
  'iec', 'pt', 'ec', 'gst', 'gstr', 'cit', 'itat', 'fc', 'gpr', 'tc', 'trs', 'fla', 'odi',
  'ptec', 'ptrc', 'lut', 'sez', 'reg', 'cr', 'ii', 'i',
]);

const titleCaseWord = (word, isFirst) => {
  const lower = word.toLowerCase();
  const bare = lower.replace(/[^a-z]/g, '');
  if (KEEP_UPPER.has(bare)) return word.toUpperCase();
  if (!isFirst && SMALL_WORDS.has(lower)) return lower;
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

export const formatServiceName = (raw) =>
  raw
    .split(' ')
    .map((token, idx) => {
      if (/[\d()@./]/.test(token)) return token;
      if (token.includes('-')) return token.split('-').map((p) => titleCaseWord(p, false)).join('-');
      return titleCaseWord(token, idx === 0);
    })
    .join(' ');

// --- Due-date rules -------------------------------------------------------
// Mirrors src/utils/dueRule.js on the backend. A rule is
// { frequency, dueDay, monthOffset } expressed relative to the period.

export const FREQUENCY_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'half_yearly', label: 'Half-yearly' },
  { value: 'annual', label: 'Annual' },
  { value: 'one_time', label: 'One-time' },
];

const FREQUENCY_LABELS = Object.fromEntries(FREQUENCY_OPTIONS.map((o) => [o.value, o.label]));
export const frequencyLabel = (freq) => FREQUENCY_LABELS[freq] || freq || '';

const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// Merge an org override onto a service's default rule.
//
// ⚠️ Where there IS a default, frequency comes from it and the override's own is
// ignored — the cadence is statutory. Where there is NONE, the override is the
// whole rule: that is the only way a service the firm invented can carry a due
// date, since nobody else will ever configure one for it. Mirrors
// src/utils/dueRule.js; keep the two in step.
export const resolveEffectiveDueRule = (defaultRule, override) => {
  if (!defaultRule) {
    if (!override || override.frequency == null || override.dueDay == null) return null;
    return {
      frequency: override.frequency,
      dueDay: override.dueDay,
      monthOffset: override.monthOffset ?? 1,
    };
  }
  const base = {
    frequency: defaultRule.frequency,
    dueDay: defaultRule.dueDay,
    monthOffset: defaultRule.monthOffset ?? 1,
  };
  if (override) {
    if (override.dueDay != null) base.dueDay = override.dueDay;
    if (override.monthOffset != null) base.monthOffset = override.monthOffset;
  }
  return base;
};

// Short chip text, e.g. "20th, +1mo". null rule => "No due date".
export const shortDueRule = (rule) => {
  if (!rule) return 'No due date';
  const off = rule.monthOffset ?? 1;
  return `${ordinal(rule.dueDay)}, +${off}mo`;
};

// Plain-English one-liner for a resolved rule.
export const describeDueRule = (rule) => {
  if (!rule) return 'No due date set';
  const day = ordinal(rule.dueDay);
  const off = rule.monthOffset ?? 1;
  const cadence = frequencyLabel(rule.frequency);
  let when;
  if (off === 0) when = `the ${day} of the period-end month`;
  else if (off === 1) when = `the ${day} of the following month`;
  else when = `the ${day}, ${off} months after period end`;
  return `${cadence} · due ${when}`;
};

// True if what the firm works to differs from the catalog. A rule set on a
// service with no default is entirely the firm's, so it always reads as custom
// — there is nothing it could be the same as.
export const overrideDiffers = (defaultRule, override) => {
  const eff = resolveEffectiveDueRule(defaultRule, override);
  if (!eff) return false;
  if (!defaultRule) return true;
  return eff.dueDay !== defaultRule.dueDay || eff.monthOffset !== (defaultRule.monthOffset ?? 1);
};

// --- Prices ---------------------------------------------------------------
// Mirrors src/utils/servicePrice.js on the backend; keep the two in step.
//
// A price is a BASE RATE for proposals, not a charge. The catalog's
// `defaultPrice` is an indicative suggestion from the platform; the firm's
// `priceOverride` is its own rate and always wins, field by field. Nothing is
// locked here — a fee, unlike a filing cadence, has no statutory half.

export const BILLING_BASIS_OPTIONS = [
  { value: 'per_filing', label: 'Per filing' },
  { value: 'per_month', label: 'Per month' },
  { value: 'per_quarter', label: 'Per quarter' },
  { value: 'per_year', label: 'Per year' },
  { value: 'one_time', label: 'One-time' },
  { value: 'hourly', label: 'Per hour' },
];

const BASIS_LABELS = {
  per_filing: 'per filing',
  per_month: 'per month',
  per_quarter: 'per quarter',
  per_year: 'per year',
  one_time: 'one-time',
  hourly: 'per hour',
};
export const billingBasisLabel = (basis) => BASIS_LABELS[basis] || basis || '';

export const FEE_KIND_OPTIONS = [
  { value: 'professional_fees', label: 'Professional fees (GST applies)' },
  { value: 'ope', label: 'Out-of-pocket expense (pure agent, no GST)' },
];

// Indian grouping — ₹1,50,000, not ₹150,000. Whole rupees unless the rate
// genuinely carries paise.
export const formatMoney = (amount, currency = 'INR') => {
  if (amount == null) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
  }).format(amount);
};

export const resolveEffectivePrice = (defaultPrice, override) => {
  const amount = override?.amount ?? defaultPrice?.amount ?? null;
  if (amount == null) return null;
  return {
    amount,
    billingBasis: override?.billingBasis ?? defaultPrice?.billingBasis ?? 'per_filing',
    kind: override?.kind ?? defaultPrice?.kind ?? 'professional_fees',
    notes: (override?.amount != null ? override?.notes : defaultPrice?.notes) || '',
    source: override?.amount != null ? 'org' : 'catalog',
  };
};

// Short chip text, e.g. "₹2,000 / filing".
export const shortPrice = (price, currency = 'INR') => {
  if (!price || price.amount == null) return 'No price';
  const unit = billingBasisLabel(price.billingBasis).replace(/^per /, '');
  return price.billingBasis === 'one_time'
    ? `${formatMoney(price.amount, currency)} once`
    : `${formatMoney(price.amount, currency)} / ${unit}`;
};

// Plain-English one-liner for a resolved price.
export const describePrice = (price, currency = 'INR') => {
  if (!price || price.amount == null) return 'No price set';
  const suffix = price.kind === 'ope' ? ' · out-of-pocket expense (no GST)' : '';
  return `${formatMoney(price.amount, currency)} ${billingBasisLabel(price.billingBasis)}${suffix}`;
};

// True if the firm's rate differs from the catalog's indicative one. A price on
// a service the catalog never priced is entirely the firm's, so it always reads
// as custom.
export const priceDiffers = (defaultPrice, override) => {
  const eff = resolveEffectivePrice(defaultPrice, override);
  if (!eff) return false;
  if (defaultPrice?.amount == null) return true;
  return (
    eff.amount !== defaultPrice.amount ||
    eff.billingBasis !== (defaultPrice.billingBasis || 'per_filing') ||
    eff.kind !== (defaultPrice.kind || 'professional_fees')
  );
};

// What to preselect in a fresh price dialog. A service the calendar files every
// month is quoted per filing; one-off engagement work is quoted once. Guessing
// from the due rule is right far more often than defaulting everything to the
// same thing, and it is only a preselection.
export const suggestBillingBasis = (effectiveDueRule) => {
  if (!effectiveDueRule || effectiveDueRule.frequency === 'one_time') return 'one_time';
  return 'per_filing';
};

// --- Concrete dates -------------------------------------------------------
// Mirrors utils/dueRule.computeDueDate + jobs/instanceSeeder.buildPeriods on
// the backend, so the admin sees the EXACT dates the engine will generate.

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Day is clamped to the target month's length, so 31 means "last day".
export const computeDueDate = (periodEnd, dueDay, monthOffset) => {
  if (dueDay == null || !periodEnd) return null;
  const offset = monthOffset ?? 1;
  const target = new Date(periodEnd.getFullYear(), periodEnd.getMonth() + offset, 1);
  const daysInMonth = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  return new Date(target.getFullYear(), target.getMonth(), Math.min(dueDay, daysInMonth));
};

// A standard FY ends in March (month index 2), so an annual rule's monthOffset
// is just "how many months past March". These two convert between the offset
// the engine stores and the month name an admin actually thinks in.
export const offsetToMonthIndex = (offset) => (2 + (offset ?? 0)) % 12;
export const monthIndexToOffset = (monthIndex) => (monthIndex - 2 + 12) % 12;

const endOfMonth = (from, offset) =>
  new Date(from.getFullYear(), from.getMonth() + offset + 1, 0);

// The real dates this rule will produce for the current financial year.
export const previewDueDates = (rule, limit = 4) => {
  if (!rule || rule.dueDay == null || rule.frequency === 'one_time') return [];

  const now = new Date();
  const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const fyStart = new Date(fyStartYear, 3, 1);
  const fyEnd = new Date(fyStartYear + 1, 2, 31);
  const shortEnd = String(fyStartYear + 1).slice(-2);

  let periods;
  if (rule.frequency === 'monthly') {
    periods = Array.from({ length: 12 }, (_, i) => {
      const m = new Date(fyStartYear, 3 + i, 1);
      return { label: `${MONTHS[m.getMonth()]}-${m.getFullYear()}`, end: endOfMonth(fyStart, i) };
    });
  } else if (rule.frequency === 'quarterly') {
    periods = [1, 2, 3, 4].map((q) => ({
      label: `Q${q} FY${String(fyStartYear).slice(-2)}-${shortEnd}`,
      end: endOfMonth(fyStart, 3 * q - 1),
    }));
  } else if (rule.frequency === 'half_yearly') {
    periods = [1, 2].map((h) => ({
      label: `H${h} FY${String(fyStartYear).slice(-2)}-${shortEnd}`,
      end: endOfMonth(fyStart, 6 * h - 1),
    }));
  } else {
    periods = [{ label: `FY ${fyStartYear}-${shortEnd}`, end: fyEnd }];
  }

  const exceptionByIndex = new Map((rule.exceptions || []).map((e) => [e.periodIndex, e]));

  return periods
    .map((p, i) => {
      const ex = exceptionByIndex.get(i);
      return {
        label: p.label,
        date: computeDueDate(p.end, ex ? ex.dueDay : rule.dueDay, ex ? ex.monthOffset : rule.monthOffset),
        isException: !!ex,
      };
    })
    .slice(0, limit);
};

export const formatDate = (d) =>
  d ? d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
